/**
 * Ausrollung Phase 2 (G-18): Jahr + KW-Bereich → Tages-Planzeilen je
 * Wochenplan-Zeile × Team-Besetzung × Kalendertag (alle 7 Tage, Konti-Betrieb).
 * Optionales Schichtmodell (empfohlen): rollt nur dessen Wochenpläne aus —
 * ohne Auswahl bleibt das Bestandsverhalten (alle Wochenpläne im Bereich).
 */

import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { isoWeekOf, todayIso, weeksInIsoYear } from "@/lib/isoWeek"
import { api, apiError } from "@/services/api"
import type { RolloutResponse } from "@/types"

interface RolloutDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function RolloutDialog({ open, onOpenChange }: RolloutDialogProps) {
  const queryClient = useQueryClient()
  const current = isoWeekOf(todayIso())
  const [year, setYear] = useState(String(current.year))
  const [fromWeek, setFromWeek] = useState(String(current.week))
  const [toWeek, setToWeek] = useState(String(current.week))
  const [shiftModelId, setShiftModelId] = useState("")
  const [result, setResult] = useState<RolloutResponse | null>(null)

  useEffect(() => {
    if (open) setResult(null)
  }, [open])

  const models = useQuery({
    queryKey: ["shiftModels", { size: 100 }],
    queryFn: () => api.organization.shiftModels.list({ size: 100 }),
    enabled: open,
  })

  const maxWeek = weeksInIsoYear(Number(year) || current.year)
  const valid =
    Number(year) >= 2000 &&
    Number(fromWeek) >= 1 &&
    Number(toWeek) <= maxWeek &&
    Number(fromWeek) <= Number(toWeek)

  const rollout = useMutation({
    mutationFn: () =>
      api.planning.rollout({
        year: Number(year),
        fromWeek: Number(fromWeek),
        toWeek: Number(toWeek),
        ...(shiftModelId !== "" ? { shiftModelId: Number(shiftModelId) } : {}),
      }),
    onSuccess: (r) => {
      setResult(r)
      queryClient.invalidateQueries({ queryKey: ["shiftPlan"] })
      toast.success(
        `Ausgerollt: ${r.created} Planzeilen neu, ${r.skippedExisting} bereits vorhanden`
      )
    },
    onError: (e) => toast.error(apiError(e)),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Schichtplan ausrollen (Phase 2)</DialogTitle>
          <DialogDescription>
            Erzeugt Tages-Planzeilen aus den Wochenplänen des Bereichs; Upsert mit
            Duplikatschutz (G-18/G-23).
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="space-y-1.5">
            <Label>Schichtmodell (optional, empfohlen)</Label>
            <Select value={shiftModelId} onChange={(e) => setShiftModelId(e.target.value)}>
              <option value="">— Alle Schichtmodelle (Bestandsverhalten) —</option>
              {(models.data?.data ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.shiftType})
                </option>
              ))}
            </Select>
            <p className="text-xs text-muted-foreground">
              Mit Auswahl werden nur die Wochenpläne dieses Modells ausgerollt.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Jahr *</Label>
              <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>KW von *</Label>
              <Input
                type="number"
                min={1}
                max={maxWeek}
                value={fromWeek}
                onChange={(e) => setFromWeek(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>KW bis *</Label>
              <Input
                type="number"
                min={1}
                max={maxWeek}
                value={toWeek}
                onChange={(e) => setToWeek(e.target.value)}
              />
            </div>
          </div>

          {result && (
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <p className="font-medium">Ergebnis</p>
              <p className="text-muted-foreground">
                {result.created} Planzeilen neu · {result.skippedExisting} übersprungen ·{" "}
                aus {result.weekPlans} Wochenplan-Zeilen
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
          <Button disabled={!valid || rollout.isPending} onClick={() => rollout.mutate()}>
            Ausrollen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
